import { useRef } from "react";

import { ChevronRightIcon } from "lucide-react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";

import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

import { NavAnchor } from "./nav-anchor";
import { ACTIVE_MARKER } from "./nav-styles";
import { type NavNode, isBranch } from "./nav-tree";
import { useHoverIntent } from "./use-hover-intent";

/**
 * Tugmadan panelgacha masofa. Tugma 2rem va 3rem li yon panelning `p-2`
 * ichida turadi: 8px panel chetigacha, qolgani — panel bilan menyu orasidagi
 * bo'shliq.
 */
const SIDE_OFFSET = 14;

/** Ichki menyu: ota qator menyuning `p-1` ichida — 4px chetgacha, qolgani bo'shliq. */
const SUB_SIDE_OFFSET = 10;

/**
 * Menyuning `p-1` i: birinchi qator tugma (ichki menyuda — ota qator) bilan
 * bir chiziqda tursin, bir necha piksel pastda emas.
 */
const ALIGN_OFFSET = -4;

/** Menyu ochiq, fokus esa hali tugmada (kursor ochgan) — bu tugmalar fokusni menyuga olib kiradi. */
const ENTER_KEYS = new Set(["Enter", " ", "ArrowDown", "ArrowRight"]);

/**
 * Menyu yuzasi — yon panelning o'zi bilan bir xil fon (`bg-primary`,
 * qorong'i mavzuda `bg-sidebar`): menyu panelning davomi bo'lib ko'rinadi,
 * begona qalqib chiquvchi oyna emas. shadcn'ning `DropdownMenuSubContent` i
 * bu yerga yaramaydi — u `dark` klassi va popover ranglarini qotirib qo'ygan.
 */
const SURFACE =
  "z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-52 overflow-x-hidden " +
  "overflow-y-auto rounded-lg bg-primary p-1 text-sidebar-foreground shadow-lg ring-1 " +
  "ring-sidebar-border dark:bg-sidebar origin-(--radix-dropdown-menu-content-transform-origin) " +
  "duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out " +
  "data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=right]:slide-in-from-left-2";

/**
 * Menyu qatori — yon paneldagi tugma bilan bir xil balandlik va ranglar.
 * Faol qator belgisi menyuning `p-1` chekkasiga chiqadi: qator ichida u
 * ikonkaga yopishib qolardi, menyudan tashqarida esa kesilardi.
 */
const ROW =
  "relative flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm outline-hidden " +
  "select-none data-highlighted:bg-sidebar-accent data-highlighted:text-sidebar-accent-foreground " +
  "data-active:bg-sidebar-accent data-active:font-medium data-active:text-sidebar-accent-foreground " +
  "data-active:before:absolute data-active:before:inset-y-1 data-active:before:-left-1 " +
  "data-active:before:w-1 data-active:before:rounded-full data-active:before:bg-sidebar-primary " +
  "data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0";

/**
 * Yig'ilgan (faqat ikonkali) paneldagi bolali band.
 *
 * Ikonkalar qatorida ichma-ich ro'yxatga joy yo'q: shadcn u yerda bola
 * bandlarni butunlay yashiradi va ularga faqat panelni yoyib yetib borilardi.
 * Endi kursor ikonka ustida to'xtasa, o'ngda sarlavhali menyu ochiladi;
 * bolasi bor qator o'z menyusini yana o'ngda ochadi.
 *
 * `onPointerEnter`/`onPointerLeave` `<li>` da turadi: React bu hodisalarni
 * DOM emas, komponentlar daraxti bo'yicha tarqatadi, ya'ni `body` ga
 * ko'chirilgan menyu ham shu `<li>` ning "ichi" hisoblanadi — kursor
 * tugmadan menyuga o'tganda menyu yopilmaydi.
 */
export function NavFlyout({ node }: { node: NavNode }) {
  const { open, source, setOpen, hoverProps } = useHoverIntent();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Radix `DropdownMenu.Content` bu ishlovchini TURIDA e'lon qilmaydi (menyu
  // odatda fokusni olishi shart), lekin prop `MenuContentImpl` gacha yetib
  // boradi — Radix'ning o'z ichki menyusi ham fokusni shu yo'l bilan
  // boshqaradi. Yangilanishda bu yo'l yopilsa, `app-sidebar.test.tsx` dagi
  // "matn maydonidagi fokusni olmaydi" testi yiqiladi.
  const openAutoFocus = {
    onOpenAutoFocus: (event: Event) => {
      const active = document.activeElement;
      previousFocus.current = active instanceof HTMLElement ? active : null;
      // Kursor bilan ochilgan menyu fokusni o'ziga tortmaydi: odam matn
      // maydonida yozib turgan bo'lsa, maydon `blur` bo'lib (forma xatosi
      // chiqib) harflari menyu qidiruviga ketardi.
      if (source === "hover") event.preventDefault();
    },
  };

  return (
    <SidebarMenuItem {...hoverProps} data-active={node.active} className={ACTIVE_MARKER}>
      <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuPrimitive.Trigger ref={triggerRef} asChild>
          {/* Tooltip yo'q: menyu sarlavhasi uning o'rnini bosadi. Menyu
              ochiq turganda tugma ham belgilangan — menyu qaysi ikonkaga
              tegishli ekani ko'rinib tursin. */}
          <SidebarMenuButton
            isActive={node.active}
            className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
            onPointerDown={event => {
              // Radix tugma bosilganda menyuni ALMASHTIRADI: kursor ochib
              // qo'ygan menyu bosishdan yopilib qolardi. Sichqoncha bilan
              // bosish — "ochiq tursin"; sensorli ekranda Radix'ning o'zi.
              if (event.pointerType !== "mouse" || event.button !== 0 || event.ctrlKey) return;
              event.preventDefault();
              setOpen(true);
            }}
            onKeyDown={event => {
              // Radix Enter/Space/ArrowDown ni biladi; o'ngga ochiladigan
              // menyu uchun ArrowRight ham tabiiy. Menyuni kursor ochgan va
              // fokus hali tugmada bo'lsa, Enter uni yopmasin — ichiga kirsin.
              if (open ? !ENTER_KEYS.has(event.key) : event.key !== "ArrowRight") return;
              event.preventDefault();
              if (open)
                contentRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
              else setOpen(true);
            }}
          >
            {node.icon && <node.icon />}
            <span>{node.label}</span>
          </SidebarMenuButton>
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            {...openAutoFocus}
            ref={contentRef}
            side="right"
            align="start"
            sideOffset={SIDE_OFFSET}
            alignOffset={ALIGN_OFFSET}
            collisionPadding={8}
            className={SURFACE}
            onCloseAutoFocus={event => {
              const active = document.activeElement;
              // Fokus menyu ichida emas edi (masalan, matn maydonida qoldi) —
              // uni joyidan qo'zg'atadigan sabab yo'q. Menyu bilan birga
              // o'chgan element esa "yo'qolgan" fokus hisoblanadi.
              if (active && active !== document.body && active.isConnected) {
                event.preventDefault();
                return;
              }
              // Klaviatura, Escape yoki band tanlash: Radix fokusni tugmaga
              // qaytaradi. Kursor ketib yopilganda esa fokus menyuga faqat
              // kursor tufayli kirgan — u oldingi egasiga qaytadi.
              if (source !== "hover") return;
              event.preventDefault();
              if (previousFocus.current?.isConnected) {
                previousFocus.current.focus({ preventScroll: true });
              }
            }}
            onPointerDownOutside={event => {
              // Tugma menyudan "tashqarida" turadi va uni bosish menyuni
              // yopib yuborardi — kursor ochgan menyu bosilganda ochiq qoladi.
              if (triggerRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            onKeyDown={event => {
              // O'ngga ochilgan menyu chapga strelka bilan yopiladi. Ichki
              // menyu bu tugmani o'zi ushlaydi va `preventDefault` qiladi.
              if (event.key !== "ArrowLeft" || event.defaultPrevented) return;
              event.preventDefault();
              setOpen(false);
            }}
          >
            <DropdownMenuPrimitive.Label className="text-sidebar-foreground/70 truncate px-2 pt-1.5 pb-1 text-xs font-semibold">
              {node.label}
            </DropdownMenuPrimitive.Label>
            <FlyoutEntries nodes={node.children ?? []} />
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    </SidebarMenuItem>
  );
}

/** Menyu qatorlari; bolasi bor qator o'z menyusini ochadi (istalgan chuqurlikda). */
function FlyoutEntries({ nodes }: { nodes: readonly NavNode[] }) {
  return nodes.map(node =>
    isBranch(node) ? (
      <DropdownMenuPrimitive.Sub key={node.key}>
        <DropdownMenuPrimitive.SubTrigger data-active={node.active} className={ROW}>
          {node.icon && <node.icon />}
          <span className="truncate">{node.label}</span>
          <ChevronRightIcon className="ml-auto" />
        </DropdownMenuPrimitive.SubTrigger>
        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.SubContent
            sideOffset={SUB_SIDE_OFFSET}
            alignOffset={ALIGN_OFFSET}
            collisionPadding={8}
            className={SURFACE}
          >
            <FlyoutEntries nodes={node.children} />
          </DropdownMenuPrimitive.SubContent>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Sub>
    ) : (
      // Haqiqiy havola: Radix faqat klaviatura va yopilishni qo'shadi.
      <DropdownMenuPrimitive.Item key={node.key} asChild>
        <NavAnchor
          href={node.href}
          aria-current={node.active ? "page" : undefined}
          data-active={node.active}
          className={ROW}
        >
          {node.icon && <node.icon />}
          <span className="truncate">{node.label}</span>
        </NavAnchor>
      </DropdownMenuPrimitive.Item>
    ),
  );
}
